import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './PlanCatalogAdmin.css';

type CustomerAttentionRecord = {
    providerId: string;
    providerName: string;
    planCode?: 'starter' | 'premium';
    reason: 'payment_failure' | 'capacity_mismatch';
    serviceAccessStatus: 'warning' | 'locked';
    paymentStatus?: string;
    subscriptionUnits?: number;
    requiredSubscriptionUnits?: number;
    availableLicenses?: number;
    regularisationDueAt?: string;
    billingOwnerEmail?: string;
    contactPhone?: string;
};

const reasonLabel = (reason: CustomerAttentionRecord['reason']): string =>
    reason === 'payment_failure' ? 'Payment failed' : 'Capacity does not cover families';

const formatDate = (value?: string): string => {
    if (!value) return 'Not set';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleDateString('en-GB');
};

/** Show Root and Super operators the self-service customers who need commercial follow-up. */
export const BillingAttentionAdmin: React.FC = () => {
    const [records, setRecords] = useState<CustomerAttentionRecord[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const callable = httpsCallable<Record<string, never>, { customers: CustomerAttentionRecord[] }>(
                getFunctions(),
                'getSaasCustomersRequiringAttention'
            );
            const result = await callable({});
            setRecords(Array.isArray(result.data.customers) ? result.data.customers : []);
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Could not load customers requiring attention.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { void load(); }, []);

    return (
        <section className='catalog-admin' aria-labelledby='billing-attention-heading'>
            <header className='catalog-admin__header'>
                <div>
                    <span className='catalog-admin__root-badge'>Root &amp; Super</span>
                    <h2 id='billing-attention-heading'>Customers requiring attention</h2>
                    <p>Payment failures and capacity mismatches remain here through the 30-day warning period and while locked.</p>
                </div>
                <button type='button' className='catalog-button catalog-button--secondary' onClick={() => void load()} disabled={isLoading}>
                    {isLoading ? 'Refreshing…' : 'Refresh'}
                </button>
            </header>
            {error && <div className='catalog-alert catalog-alert--error' role='alert'>{error}</div>}
            {!error && !isLoading && records.length === 0 && (
                <div className='catalog-alert' role='status'>No self-service customers currently require follow-up.</div>
            )}
            {records.length > 0 && (
                <div className='catalog-attention-table-wrap'>
                    <table className='catalog-attention-table'>
                        <thead><tr>
                            <th>Customer</th><th>Plan</th><th>Issue</th><th>Status</th><th>Due</th><th>Capacity</th><th>Contact</th>
                        </tr></thead>
                        <tbody>
                            {records.map((record) => (
                                <tr key={record.providerId}>
                                    <td><strong>{record.providerName}</strong><br /><small>{record.providerId}</small></td>
                                    <td>{record.planCode === 'starter' ? 'Free trial' : 'Premium'}</td>
                                    <td>{reasonLabel(record.reason)}</td>
                                    <td>{record.serviceAccessStatus === 'locked' ? 'Locked' : 'Warning'}</td>
                                    <td>{formatDate(record.regularisationDueAt)}</td>
                                    <td>
                                        {record.subscriptionUnits || 1} unit{record.subscriptionUnits === 1 ? '' : 's'}
                                        {record.requiredSubscriptionUnits ? ` · needs ${record.requiredSubscriptionUnits}` : ''}
                                    </td>
                                    <td>
                                        {record.billingOwnerEmail ? <a href={`mailto:${record.billingOwnerEmail}`}>{record.billingOwnerEmail}</a> : 'No email'}
                                        {record.contactPhone && <><br /><a href={`tel:${record.contactPhone}`}>{record.contactPhone}</a></>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};
